import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { getUserAccounts } from 'backoffice/users/_redux';

import {
  Collapsible,
  NoItemsPanel,
  BaseSelectField,
  Calendar,
  StatusCell,
  TableLoader,
  DateCell,
  ReactDataTable,
} from 'common/components';
import noPaymentsImage from 'common/images/noPayments.svg';

import { withTheme } from 'common/styling/theme';
import { getOpenOrders, getClosedOrders, getDelayedOrders, getDeferredOrders } from '../../payments/_redux';

import { staticStyles, getDynamicStyles } from './style';

const startDate = moment().startOf('day').unix();

const endDate = moment().endOf('day').unix();

const startCalendar = moment().startOf('day');

const endCalendar = moment().endOf('day');

// const getFixedNumber = (num, decimal) => Number(Number(num).toFixed(decimal));

const UserTradingHistory = ({
  accounts,
  accountsAreLoaded,
  getUserAccounts,
  getOpenOrders,
  getClosedOrders,
  getDelayedOrders,
  getDeferredOrders,
  orders,
  ordersAreLoaded,
  userId,
  accountId,
  accountLabel,
  theme,
  opened,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const [selectedAccount, setSelectedAccount] = useState({ value: accountId, label: accountLabel });
  const selectedAccountRef = useRef(selectedAccount);

  const isMt5Account = accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'mt5' ?? false;
  const isInvestMt5Account =
    accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'investmt5' ?? false;
  const isCfhAccount = accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'cfh' ?? false;
  const isMt4Account = accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'mt4' ?? false;
  const isYbAccount = accounts.find(account => account.id === selectedAccount.value)?.platformSlug === 'yb' ?? false;

  const openOrdersSupported = !isCfhAccount && !isYbAccount;
  const [activeTab, setActiveTab] = useState(openOrdersSupported ? 'opened' : 'closed');

  const shouldHideCalendar =
    ((isMt5Account || isInvestMt5Account) && activeTab === 'opened') || activeTab === 'pending';

  const selectedAccountInfo = useMemo(
    () => accounts.find(account => account.id === selectedAccount.value),
    [selectedAccount, accounts]
  );

  const memoizedOnChangeParams = useMemo(
    () => ({
      clientId: userId,
      accountId: selectedAccount.value,
      activeTab,
    }),
    [userId, accountId, activeTab]
  );

  useEffect(() => {
    getUserAccounts({ userId, noDetails: true });
  }, [getUserAccounts, userId]);

  const handleOpenCollapse = () => {
    if (isCfhAccount || isYbAccount) {
      setActiveTab('closed');
      getClosedOrders({
        userId,
        accountId: selectedAccount.value,
        start: startDate,
        end: endDate,
        accountPlatformSlug: selectedAccountInfo?.platformSlug,
      });
    } else {
      getOpenOrders({
        userId,
        accountId: selectedAccount.value,
        start: startDate,
        end: endDate,
        accountPlatformSlug: selectedAccountInfo?.platformSlug,
      });
    }
  };

  const CellWEmpty = ({ value }) => (value || value === 0 ? <span>{value}</span> : <span>一</span>);

  CellWEmpty.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  CellWEmpty.defaultProps = {
    value: '',
  };

  const DateWEmpty = ({ value }) => (value ? <DateCell value={value} /> : <span>一</span>);

  DateWEmpty.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  };

  DateWEmpty.defaultProps = {
    value: '',
  };
  /* eslint-disable */
  const order = {
    Header: 'justOrder',
    Cell: ({
      row: {
        original: { id },
      },
    }) => <CellWEmpty value={id} />,
    Footer: (
      <span className="summary__order">
        <FormattedMessage id="justTotal">{txt => txt}</FormattedMessage>
      </span>
    ),
    isPreviewCell: true,
  };
  const symbol = {
    Header: 'justSymbol',
    Cell: ({
      row: {
        original: { symbol },
      },
    }) => <CellWEmpty value={symbol} />,
    isPreviewCell: true,
  };
  const status = {
    Header: 'justType',
    Cell: ({
      row: {
        original: { command },
      },
    }) => <StatusCell statusCode={command?.toLowerCase().replace('/', '')} noDot />,
    isPreviewCell: true,
  };
  const openTime = {
    Header: 'openTime',
    Cell: ({
      row: {
        original: { open_time },
      },
    }) => <DateWEmpty value={open_time} />,
  };
  const closeTime = {
    Header: 'justCloseTime',
    Cell: ({
      row: {
        original: { close_time, expiration },
      },
    }) => <DateWEmpty value={close_time || expiration} />,
  };
  const openPrice = {
    Header: 'justOpenPrice',
    Cell: ({
      row: {
        original: { open_price },
      },
    }) => <CellWEmpty value={open_price} />,
  };
  const closePrice = {
    Header: document.location.hostname === 'portal.rock-west.com' ? 'justPrice' : 'justClosePrice',
    Cell: ({
      row: {
        original: { close_price },
      },
    }) => <CellWEmpty value={close_price} />,
  };
  const lots = {
    Header: 'justLots',
    Cell: ({
      row: {
        original: { lots, command },
      },
    }) => <CellWEmpty value={lots === 0.01 && command === 'In/Out' ? 0 : lots} />,
    Footer: (
      <div className="summary__lots">
        <span className="subtext">
          <FormattedMessage id="justLots">{txt => txt}</FormattedMessage>
        </span>
        <div>{orders.summary ? orders.summary.lots : ''}</div>
      </div>
    ),
  };

  const commission = {
    Header: 'justCommission',
    Cell: ({
      row: {
        original: { commission },
      },
    }) => <CellWEmpty value={commission} />,
    Footer: (
      <div className="summary__commission">
        <span className="subtext">
          <FormattedMessage id="justCommission">{txt => txt}</FormattedMessage>
        </span>
        <div>{orders.summary ? orders.summary.commission : ''}</div>
      </div>
    ),
  };
  const swap = {
    Header: 'justSwap',
    Cell: ({
      row: {
        original: { swap },
      },
    }) => <CellWEmpty value={swap} />,
    Footer: (
      <div className="summary__swap">
        <span className="subtext">
          <FormattedMessage id="justSwap">{txt => txt}</FormattedMessage>
        </span>
        <div>{orders.summary ? orders.summary.swap : ''}</div>
      </div>
    ),
  };
  const profit = {
    Header: 'justProfit',
    Cell: ({
      row: {
        original: { profit },
      },
    }) => <CellWEmpty value={profit} />,
    Footer: (
      <div className="summary__profit">
        <span className="subtext">
          <FormattedMessage id="justProfit">{txt => txt}</FormattedMessage>
        </span>
        <div>{orders.summary ? orders.summary.profit : ''}</div>
      </div>
    ),
  };
  /* eslint-enable */

  const columns = [order, symbol, status, openTime, closeTime, openPrice, closePrice, lots, commission, swap, profit];

  const handleAccountChange = ({ value, label }) => {
    setSelectedAccount({ value, label });
    selectedAccountRef.current = { value, label };
  };

  useEffect(() => {
    if (selectedAccountRef.current.value && selectedAccountInfo) {
      switch (activeTab) {
        case 'closed':
          getClosedOrders({
            userId,
            accountId: selectedAccountRef.current.value,
            start: startDate,
            end: endDate,
            accountPlatformSlug: selectedAccountInfo?.platformSlug,
          });
          break;
        case 'pending':
          if (isMt5Account) {
            getDeferredOrders({ userId, accountId: selectedAccountRef.current.value, start: startDate, end: endDate });
          } else {
            getDelayedOrders({ userId, accountId: selectedAccountRef.current.value });
          }
          break;
        default:
          if (isCfhAccount || isYbAccount) {
            setActiveTab('closed');
            getClosedOrders({
              userId,
              accountId: selectedAccountRef.current.value,
              start: startDate,
              end: endDate,
              accountPlatformSlug: selectedAccountInfo?.platformSlug,
            });
          } else {
            getOpenOrders({
              userId,
              accountId: selectedAccountRef.current.value,
              start: startDate,
              end: endDate,
              accountPlatformSlug: selectedAccountInfo?.platformSlug,
            });
          }
      }
    }
  }, [selectedAccountRef.current, selectedAccountInfo]);

  const handleChangeTab = activeTab => {
    switch (activeTab) {
      case 'closed':
        getClosedOrders({
          userId,
          accountId: selectedAccount.value,
          start: startDate,
          end: endDate,
          accountPlatformSlug: selectedAccountInfo?.platformSlug,
        });
        break;
      case 'pending':
        if (isMt5Account) {
          getDeferredOrders({ userId, accountId: selectedAccount.value, start: startDate, end: endDate });
        } else {
          getDelayedOrders({ userId, accountId: selectedAccount.value });
        }
        break;
      default:
        getOpenOrders({
          userId,
          accountId: selectedAccount.value,
          start: startDate,
          end: endDate,
          accountPlatformSlug: selectedAccountInfo?.platformSlug,
        });
    }
    setActiveTab(activeTab);
  };

  const handleChangeDate = (userId, accountId, activeTab, startDate, endDate) => {
    switch (activeTab) {
      case 'closed':
        getClosedOrders({
          userId,
          accountId,
          start: moment(startDate).format('x'),
          end: moment(endDate).format('x'),
          accountPlatformSlug: selectedAccountInfo?.platformSlug,
        });
        break;
      case 'pending':
        if (isMt5Account) {
          getDeferredOrders({
            userId,
            accountId,
            start: moment(startDate).format('x'),
            end: moment(endDate).format('x'),
          });
        } else {
          getDelayedOrders({
            userId,
            accountId,
            start: moment(startDate).format('x'),
            end: moment(endDate).format('x'),
          });
        }
        break;
      default:
        getOpenOrders({
          userId,
          accountId,
          start: moment(startDate).format('x'),
          end: moment(endDate).format('x'),
          accountPlatformSlug: selectedAccountInfo?.platformSlug,
        });
    }
  };

  useEffect(() => {
    setSelectedAccount(prev => (prev.value ? { ...prev } : { value: accounts[0]?.id, label: accounts[0]?.login }));
  }, [accounts, accountsAreLoaded]);

  return (
    <div className="UserTradingHistory">
      {!!accounts.length && accountsAreLoaded && (
        <Collapsible captionId="justTradingHistory" handleOpen={handleOpenCollapse} opened={opened}>
          <div className="UserTradingHistory__select">
            <BaseSelectField
              value={selectedAccount}
              name="userAccount"
              options={accounts.map(account => ({ value: account.id, label: account.login }))}
              onChange={value => handleAccountChange(value)}
              textId="selectAccount"
              disabled={!accountsAreLoaded}
            />
          </div>
          <div className="UserTradingHistory__heading">
            <div className="UserTradingHistory__tabs">
              {openOrdersSupported && (
                <FormattedMessage id="justOpenOrders">
                  {txt => (
                    <button
                      type="button"
                      className={classNames('UserTradingHistory__tab', {
                        active: activeTab === 'opened',
                      })}
                      onClick={() => handleChangeTab('opened')}>
                      {txt}
                    </button>
                  )}
                </FormattedMessage>
              )}
              <FormattedMessage id={openOrdersSupported ? 'justClosed' : 'justDeals'}>
                {txt => (
                  <button
                    type="button"
                    className={classNames('UserTradingHistory__tab', {
                      active: activeTab === 'closed',
                    })}
                    onClick={() => handleChangeTab('closed')}>
                    {txt}
                  </button>
                )}
              </FormattedMessage>
              <FormattedMessage id="justPending">
                {txt => (
                  <button
                    type="button"
                    className={classNames('UserTradingHistory__tab', {
                      active: activeTab === 'pending',
                    })}
                    onClick={() => handleChangeTab('pending')}>
                    {txt}
                  </button>
                )}
              </FormattedMessage>
            </div>
            {!shouldHideCalendar && (
              <Calendar
                daysLimit={activeTab === 'closed' && isMt4Account ? 1 : 30}
                onChangeFunc={handleChangeDate}
                onChangeParams={memoizedOnChangeParams}
                defaultStartDate={startCalendar}
                defaultEndDate={endCalendar}
                setCorrectLastDay
              />
            )}
          </div>
          {!ordersAreLoaded && <TableLoader />}
          {ordersAreLoaded && (
            <div>
              {orders?.items?.length || (Array.isArray(orders) && orders.length) ? (
                <ReactDataTable columns={columns} data={orders?.items} onRowClick={() => {}} isResponsive isInfinite />
              ) : (
                <NoItemsPanel icon={noPaymentsImage} captionSlug="noData" />
              )}
            </div>
          )}
        </Collapsible>
      )}

      {!accounts.length && accountsAreLoaded && (
        <Collapsible captionId="justTradingHistory" handleOpen={() => {}} opened={opened}>
          <NoItemsPanel icon={noPaymentsImage} captionSlug="justNoOrders" />
        </Collapsible>
      )}
      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

UserTradingHistory.propTypes = {
  accounts: PropTypes.array.isRequired,
  accountsAreLoaded: PropTypes.bool.isRequired,
  getUserAccounts: PropTypes.func.isRequired,
  getOpenOrders: PropTypes.func.isRequired,
  getClosedOrders: PropTypes.func.isRequired,
  getDelayedOrders: PropTypes.func.isRequired,
  getDeferredOrders: PropTypes.func.isRequired,
  orders: PropTypes.object.isRequired,
  ordersAreLoaded: PropTypes.bool.isRequired,
  accountId: PropTypes.number.isRequired,
  accountLabel: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  opened: PropTypes.bool,
  theme: PropTypes.object,
};

UserTradingHistory.defaultProps = {
  opened: false,
  theme: {},
};
export default compose(
  withTheme(),
  connect(
    state => ({
      accounts: state.backoffice.users.accounts.filter(account => !account.isArchived),
      accountsAreLoaded: state.backoffice.users.accountsAreLoaded,
      orders: state.backoffice.payments.orders,
      ordersAreLoaded: state.backoffice.payments.ordersAreLoaded,
    }),
    {
      getUserAccounts: data => getUserAccounts.request(data),
      getOpenOrders: data => getOpenOrders.request(data),
      getClosedOrders: data => getClosedOrders.request(data),
      getDelayedOrders: data => getDelayedOrders.request(data),
      getDeferredOrders: data => getDeferredOrders.request(data),
    }
  )
)(UserTradingHistory);
export { UserTradingHistory };
